import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "@akuna/ui";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Autocomplete } from "@/components/ui/autocomplete";
import { MedicationAutocomplete, type MedicationWithRelations } from "../MedicationAutocomplete";
import { useMedicationDosages, useMedications } from "@/hooks/useMedications";
import {
  buildDefaultPrescriptionItem,
  buildPosologySentence,
} from "@/lib/prescriptions/editorDraft";
import { isSingularCount, parsePosologyCount, resolveCalendarUnitForCount } from "@/lib/prescriptions/posology-units";
import type { PrescriptionItemInput } from "@/lib/validations/medical-prescription";
import type { Database } from "@/integrations/supabase/types";
import { cn } from "@/lib/utils";
import { ExpandedItemShell } from "./ExpandedItemShell";

type MedicationRow = Database["public"]["Tables"]["medications"]["Row"];

const lookupName = (value: unknown): string => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object" && "name" in value) {
    const name = (value as { name?: unknown }).name;
    return typeof name === "string" ? name : "";
  }
  return "";
};

export function MedicationItemEditor({
  initial,
  ordinal,
  clinicId,
  speciesId,
  mobile = false,
  onCommit,
  onCancel,
  onDraftChange,
  onOpenCreateMedication,
}: {
  initial: PrescriptionItemInput | null;
  ordinal: number;
  clinicId: string;
  speciesId?: string;
  mobile?: boolean;
  onCommit: (item: PrescriptionItemInput) => void;
  onCancel: () => void;
  onDraftChange: (item: PrescriptionItemInput | null) => void;
  onOpenCreateMedication: (name: string, onCreated: (medication: MedicationRow) => void) => void;
}) {
  const { t, i18n } = useTranslation("prescriptions");
  const lang = i18n.language?.toLowerCase().startsWith("pt") ? "pt" : "en";
  const isEdit = Boolean(initial);
  const [item, setItem] = useState<PrescriptionItemInput>(() => initial ?? buildDefaultPrescriptionItem());
  const [finalText, setFinalText] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [nameError, setNameError] = useState<string | null>(null);
  const mode: "fields" | "free" = item.posology_mode === "free_text" ? "free" : "fields";

  const { data: medications = [], isLoading: loadingMeds } = useMedications({ searchTerm: search, includeControlled: true });
  const { data: dosages = [] } = useMedicationDosages(item.medication_id || undefined, speciesId);
  const patch = (changes: Partial<PrescriptionItemInput>) => setItem((current) => ({ ...current, ...changes }));

  useEffect(() => {
    onDraftChange(item.medication_name.trim() ? item : null);
  }, [item, onDraftChange]);
  useEffect(() => () => onDraftChange(null), [onDraftChange]);

  const sentence = useMemo(() => buildPosologySentence({ ...item, posology_mode: "automatic" }, lang), [item, lang]);
  const doseUnits = ["tablet", "capsule", "ml", "drop", "sachet", "puff"].map((k) => t(`composer.optionSets.doseUnits.${k}`));
  const routes = ["oral", "subcutaneous", "intramuscular", "intravenous", "topical", "ophthalmic", "otic", "transdermal"].map((k) => t(`composer.optionSets.routes.${k}`));
  const asOptions = (values: string[]) => values.map((v) => ({ label: v, value: v }));
  const freqCount = parsePosologyCount(item.frequency_value ?? "");
  const durCount = parsePosologyCount(item.duration_value ?? "");
  const freqUnits = asOptions(
    (freqCount != null && isSingularCount(freqCount) ? ["hour", "day", "week", "month"] : ["hours", "days", "weeks", "months"]).map((k) =>
      t(`composer.optionSets.frequencyUnits.${k}`),
    ),
  );
  const durUnits = asOptions(
    (durCount != null && isSingularCount(durCount) ? ["day", "week", "month"] : ["days", "weeks", "months"]).map((k) =>
      t(`composer.optionSets.durationUnits.${k}`),
    ),
  );
  const alignUnit = (unit: string | null | undefined, value: string) =>
    unit ? resolveCalendarUnitForCount(unit, value, i18n.language) : unit ?? "";

  const suggestions = useMemo(
    () =>
      dosages.map((entry) => ({
        id: entry.id,
        label: [
          entry.min_dose && entry.max_dose ? `${entry.min_dose}-${entry.max_dose} ${entry.dose_unit ?? ""}` : entry.min_dose ? `${entry.min_dose} ${entry.dose_unit ?? ""}` : null,
          entry.frequency_hint,
        ].filter(Boolean).join(" · "),
        route: lookupName(entry.administration_route ?? entry.med?.administration_route ?? null) || null,
        doseUnit: entry.dose_unit ?? null,
      })),
    [dosages],
  );

  const handleSelectMedication = (medication: MedicationRow, displayName?: string) => {
    const typed = medication as unknown as MedicationWithRelations;
    setItem((current) => ({
      ...current,
      medication_id: medication.id,
      medication_name: displayName ?? medication.name,
      medication_concentration: medication.concentration ?? "",
      pharmaceutical_form: lookupName(typed.pharmaceutical_form),
      administration_route: current.administration_route || lookupName(typed.administration_route),
      dose_unit: current.dose_unit || (medication.dose_unit ?? ""),
    }));
    setNameError(null);
    setSearch("");
  };

  const dirty = useMemo(
    () => JSON.stringify(item) !== JSON.stringify(initial ?? buildDefaultPrescriptionItem()),
    [item, initial],
  );

  const handleConfirm = () => {
    if (!item.medication_name.trim()) {
      setNameError(t("editor.med.nameRequired"));
      return;
    }
    const committed: PrescriptionItemInput =
      mode === "fields" && finalText !== null && finalText.trim() && finalText.trim() !== sentence
        ? { ...item, posology_mode: "free_text", free_text_posology: finalText.trim() }
        : item;
    onCommit(committed);
  };

  const label = "text-sm font-medium";
  const inputCls = mobile ? "h-12 text-base" : undefined;
  const idp = `med-item-${ordinal}`;
  const doseValueOptions = ["1/4", "1/2", "1", "2", "3"].map((v) => ({ label: v, value: v }));
  const freqValueOptions = ["1", "2", "4", "6", "8", "12", "24"].map((v) => ({ label: v, value: v }));
  const durValueOptions = ["1", "3", "5", "7", "10", "14", "21", "30"].map((v) => ({ label: v, value: v }));
  const noSuggestions = t("composer.noSuggestions");

  return (
    <ExpandedItemShell
      title={isEdit ? t("editor.med.editTitle") : t("editor.med.addTitle")}
      ordinal={ordinal}
      mobile={mobile}
      dirty={dirty}
      onCancel={onCancel}
      onConfirm={handleConfirm}
      confirmLabel={isEdit ? t("editor.med.saveItem") : t("editor.med.addToPrescription")}
      mode={{
        value: mode,
        onChange: (value) => patch({ posology_mode: value === "free" ? "free_text" : "automatic" }),
        labels: {
          fields: t("editor.mode.fields"),
          fieldsHint: t("editor.mode.fieldsHint"),
          free: t("editor.mode.free"),
          freeHint: t("editor.mode.freeHint"),
        },
      }}
    >
      <div className="space-y-2">
        <Label htmlFor={`${idp}-name`} className={label}>{t("composer.medication")}</Label>
        <MedicationAutocomplete
          medications={medications as MedicationWithRelations[]}
          isLoading={loadingMeds}
          value={item.medication_name}
          selectedMedicationId={item.medication_id ?? ""}
          popoverPortalled={false}
          onFocus={() => {
            if (!item.medication_name.trim()) setSearch("");
          }}
          onChange={(value) => {
            setSearch(value);
            setNameError(null);
            patch(
              item.medication_id
                ? { medication_id: "", medication_name: value, medication_concentration: "", pharmaceutical_form: "", administration_route: "", presentation: "" }
                : { medication_name: value },
            );
          }}
          onSelectMedication={(medication, displayName) => handleSelectMedication(medication, displayName)}
          onOpenCreateDialog={(name) => onOpenCreateMedication(name, (medication) => handleSelectMedication(medication, medication.name))}
          placeholder={t("medicationAutocomplete.placeholder")}
        />
        {nameError ? <p className="text-sm text-destructive" role="alert">{nameError}</p> : null}
      </div>

      {mode === "fields" ? (
        <>
          <div className={cn("grid gap-4", !mobile && "grid-cols-2")}>
            <div className="space-y-2">
              <Label className={label}>{t("composer.administrationRoute")}</Label>
              <Autocomplete options={asOptions(routes)} value={item.administration_route ?? ""} onChange={(v) => patch({ administration_route: v })} placeholder={t("composer.selectOrType")} emptyMessage={noSuggestions} popoverPortalled={false} />
            </div>
            <div className="space-y-2">
              <Label className={label}>{t("print.dosage")}</Label>
              <div className="grid grid-cols-2 gap-2">
                <Autocomplete options={doseValueOptions} value={item.dose_amount ?? ""} onChange={(v) => patch({ dose_amount: v })} placeholder={t("composer.doseAmountPlaceholder")} emptyMessage={noSuggestions} popoverPortalled={false} />
                <Autocomplete options={asOptions(doseUnits)} value={item.dose_unit ?? ""} onChange={(v) => patch({ dose_unit: v })} placeholder={t("composer.doseUnit")} emptyMessage={noSuggestions} popoverPortalled={false} />
              </div>
            </div>
            <div className="space-y-2">
              <Label className={label}>{t("print.frequency")}</Label>
              <div className="grid grid-cols-2 gap-2">
                <Autocomplete options={freqValueOptions} value={item.frequency_value ?? ""} onChange={(v) => patch({ frequency_value: v, frequency_unit: alignUnit(item.frequency_unit, v) })} placeholder={t("composer.frequencyValuePlaceholder")} emptyMessage={noSuggestions} popoverPortalled={false} />
                <Autocomplete options={freqUnits} value={item.frequency_unit ?? ""} onChange={(v) => patch({ frequency_unit: v })} placeholder={t("composer.frequencyUnit")} emptyMessage={noSuggestions} popoverPortalled={false} />
              </div>
            </div>
            <div className="space-y-2">
              <Label className={label}>{t("print.duration")}</Label>
              <div className="grid grid-cols-2 gap-2">
                <Autocomplete options={durValueOptions} value={item.duration_value ?? ""} onChange={(v) => patch({ duration_value: v, duration_unit: alignUnit(item.duration_unit, v) })} placeholder={t("composer.durationValuePlaceholder")} emptyMessage={noSuggestions} popoverPortalled={false} />
                <Autocomplete options={durUnits} value={item.duration_unit ?? ""} onChange={(v) => patch({ duration_unit: v })} placeholder={t("composer.durationUnit")} emptyMessage={noSuggestions} popoverPortalled={false} />
              </div>
            </div>
          </div>

          {suggestions.length > 0 ? (
            <div className="space-y-2 rounded-xl bg-secondary/60 p-3 text-xs text-muted-foreground">
              <p className="font-medium text-foreground">{t("composer.speciesDoseSuggestions")}</p>
              <div className="flex flex-wrap gap-2">
                {suggestions.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    className="rounded-lg border bg-card px-2.5 py-1 text-xs font-semibold hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    onClick={() => {
                      const first = (s.label ?? "").split(" ")[0] ?? "";
                      patch({
                        ...(first ? { dose_amount: first } : {}),
                        ...(s.doseUnit ? { dose_unit: s.doseUnit } : {}),
                        ...(s.route ? { administration_route: s.route } : {}),
                      });
                    }}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          <div className="space-y-1.5">
            <Label htmlFor={`${idp}-text`} className={label}>{t("editor.med.finalText")}</Label>
            <Textarea id={`${idp}-text`} rows={2} className={cn("rounded-[10px]", mobile && "text-base")} value={finalText ?? sentence} onChange={(e) => setFinalText(e.target.value)} />
          </div>
        </>
      ) : (
        <>
          <div className="space-y-1.5">
            <Label htmlFor={`${idp}-free`} className={label}>{t("editor.med.howToUse")}</Label>
            <Textarea id={`${idp}-free`} rows={mobile ? 5 : 7} className={cn("rounded-xl border-2 border-primary", mobile && "text-base")} value={item.free_text_posology ?? ""} onChange={(e) => patch({ free_text_posology: e.target.value })} />
            <p className="text-xs text-muted-foreground">{t("editor.med.howToUseHint")}</p>
          </div>
        </>
      )}
    </ExpandedItemShell>
  );
}
